import React, { createContext, useCallback, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PageFilterContext } from './PageFilterContext';
import { ParsedVendor } from '../data/vendorData';
import { BudgetCategory } from '../types/ui/Budget';
import { loadVendorData } from '../data/VendorLoader';
import { refreshBudgetFromApi } from '../utils/budgetStorage';
import { loadTasks, Task } from '../services/TasksService';
import { loadInventoryItems, InventoryItem } from '../services/InventoryService';
import { formatDecimal } from '../utils/decimal';
import { Song, loadSongs, refreshSongsFromApi } from '../services/SongsService';
import { Guideline, loadGuidelines, refreshGuidelinesFromApi } from '../services/GuidelinesService';
import { DecorItem } from '../types/Decor';
import { loadDecorItems, refreshDecorFromApi } from '../services/DecorService';
import { apiGet , planningPath } from '../services/PlanningApiClient';
import { useRole } from '../hooks/useRole';
import { useMoney } from '../hooks/useMoney';
import { loadQuotes } from '../services/VendorQuoteService';
import { VendorQuote } from '../types/ui/VendorQuote';
import { refreshBundlesFromApi } from '../services/BundleService';
import { UnifiedBundle, Product } from '../types/Product';
import { refreshProductsFromApi } from '../services/ProductService';
import { loadContributors } from '../services/ContributorService';
import { Contributor } from '../types/ui/Settlement';
import { getVenues, Venue } from '../services/VenueService';
import { getFloorPlans, FloorPlan } from '../services/FloorPlanService';
import {
  refreshVehiclesFromApi,
  refreshDriversFromApi,
  refreshAssignmentsFromApi,
} from '../services/LogisticsService';
import { Vehicle, Driver, GuestAssignment } from '../types/ui/Logistics';
import { refreshMenuFromApi } from '../services/MenuService';
import { genericMatch } from '../utils/searchMatch';
import { recordId } from '../utils/recordId';
import { matchAccountRoute } from '../utils/accountRoutes';
import { accountResultPath, searchAccountRecords, type AccountSearchType, type AccountSearchResult } from '../utils/accountSearch';
import { openInProject } from '../utils/openInProject';
import { getActiveProjectId } from '../utils/session';
import { errorMessageOf } from '../utils/apiErrors';
import { useAuth } from './AuthContext';
import { useNotifications } from '../components/NotificationProvider';
import { useAccountSearchData, type AccountSearchStatus } from '../hooks/useAccountSearchData';

/** A menu item flattened out of its MenuCategory, for indexing. */
interface MenuItemSearch {
  id: string;
  name: string;
  category: string;
  tab: string;
  desc: string;
  tags: string[];
  allowance?: number;
}

/** A payment flattened out of its budget category + vendor line, for indexing. */
interface PaymentSearch {
  id: string;
  amount: number;
  date: string;
  method: string;
  status: string;
  reference?: string;
  paidBy?: string;
  notes?: string;
  vendorName: string;
  categoryName: string;
}

interface StationeryItemSearch {
  id: string;
  name: string;
  category: string;
  // Raw API row field names (GET /stationery does a bare `SELECT *`, no
  // renaming) — NOT designer/printVendor/status, which don't exist on the
  // row and left these fields permanently undefined. StationeryTracker.tsx's
  // normaliseApiItem() maps these same columns to designer/printVendor/status
  // for display elsewhere in the app; this type intentionally stays close to
  // the wire shape instead of importing that mapping, since this is the only
  // consumer of StationeryItemSearch.
  assigned_to: string;
  vendor: string;
  design_status: string;
  print_status: string;
  archived?: boolean;
}

export interface SearchResult {
  id: string;
  type:
    | 'vendor' | 'budget' | 'menu' | 'task' | 'favour'
    | 'song' | 'guideline' | 'decor' | 'stationery'
    // Added: previously-unindexed entities
    | 'quote' | 'payment' | 'contributor' | 'bundle' | 'product'
    | 'venue' | 'floorplan' | 'inventory' | 'vehicle' | 'driver' | 'movement'
    // Account-level screens (see utils/accountSearch.ts)
    | AccountSearchType;
  title: string;
  description: string;
  category?: string;
  data: any;
}

interface SearchContextType {
  query: string;
  setQuery: (query: string) => void;
  results: SearchResult[];
  isSearching: boolean;
  clearSearch: () => void;
  /**
   * The term the header search has handed to whatever page is on screen.
   * Distinct from `query`, which drives the jump-to-record dropdown: pressing
   * Enter means "narrow what I'm already looking at" rather than "take me
   * somewhere else", and the two want different lifetimes.
   */
  pageFilter: string;
  setPageFilter: (term: string) => void;
  globalData: {
    vendors: ParsedVendor[];
    budgetCategories: BudgetCategory[];
    menuItems: MenuItemSearch[];
    tasks: Task[];
    favours: InventoryItem[];
    songs: Song[];
    guidelines: Guideline[];
    decorItems: DecorItem[];
    stationeryItems: StationeryItemSearch[];
    quotes: VendorQuote[];
    contributors: Contributor[];
    bundles: UnifiedBundle[];
    products: Product[];
    venues: Venue[];
    floorPlans: FloorPlan[];
    inventoryItems: InventoryItem[];
    vehicles: Vehicle[];
    drivers: Driver[];
    movements: GuestAssignment[];
  };
  /**
   * Whether the signed-in user may see money. Financial entities are neither
   * fetched nor indexed when this is false — see the RBAC note in the provider.
   */
  canViewFinancials: boolean;
  /** True on the workspace-level screens, where search covers clients, projects, invoices and the rest. */
  accountScope: boolean;
  /** Whether the account index is still loading, or a list of it failed. */
  accountStatus: AccountSearchStatus;
  /** Opens an account-level result: its record page, or its project (switching to it first when needed). */
  openAccountResult: (result: SearchResult) => Promise<void>;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};

interface SearchProviderProps {
  children: ReactNode;
}

/** Per-entity-type result cap, so one noisy type can't crowd out the rest. */
const PER_TYPE_LIMIT = 6;
/** Overall result cap (unchanged). */
const MAX_RESULTS = 30;

export const SearchProvider: React.FC<SearchProviderProps> = ({ children }) => {
  const money = useMoney();
  /** Amount for a result description, in the project currency. */
  const ngn = (v: number | null | undefined): string => money.format(Number(v ?? 0));
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [pageFilter, setPageFilter] = useState('');

  // A page filter belongs to the page it was typed on. Carrying it across a
  // navigation would silently hide rows on a page the user never filtered,
  // and the header input is the only place the term is visible.
  const { pathname } = useLocation();
  useEffect(() => { setPageFilter(''); }, [pathname]);

  // On the workspace-level screens (Clients, Projects, Invoices, ...) search
  // covers the account's records instead of one project's. The project index
  // is neither fetched nor searched there.
  const accountScope = matchAccountRoute(pathname) !== null;

  // ── RBAC ────────────────────────────────────────────────────────────────────
  // Money is hidden from users explicitly marked financially blind. That flag
  // is per-user on the workspace's `users` row and is now surfaced by
  // /planning/me, so a plain viewer keeps access to budget/quote results while
  // a financially-blind collaborator is excluded — the distinction the client
  // previously couldn't make. Gated data is never fetched, not merely hidden.
  const { canViewFinancials, isAdmin } = useRole();
  const { workspaces, switchWorkspace } = useAuth();
  const { showError } = useNotifications();
  const navigate = useNavigate();
  const { data: accountData, status: accountStatus } = useAccountSearchData({
    active: accountScope && query.trim() !== '',
    projects: workspaces,
    isAdmin,
  });

  const openAccountResult = useCallback(async (result: SearchResult) => {
    try {
      if (result.type === 'project') {
        const projectId = String(result.data?.id ?? '');
        if (!projectId) return;
        // Already the active project: go straight in rather than do nothing.
        await openInProject({ target: '/dashboard', projectId, activeProjectId: getActiveProjectId(), navigate, switchWorkspace });
        return;
      }
      const target = accountResultPath(result as AccountSearchResult);
      if (!target) return;
      await openInProject({
        target,
        projectId: result.type === 'accountVendor' ? ((result.data?.projectId as string | null) ?? null) : null,
        activeProjectId: getActiveProjectId(),
        navigate,
        switchWorkspace,
      });
    } catch (err) {
      showError(errorMessageOf(err, "Couldn't open that project. Please try again."));
    }
  }, [navigate, switchWorkspace, showError]);

  const [globalData, setGlobalData] = useState({
    vendors: [] as ParsedVendor[],
    budgetCategories: [] as BudgetCategory[],
    menuItems: [] as MenuItemSearch[],
    tasks: [] as Task[],
    favours: [] as InventoryItem[],
    songs: [] as Song[],
    guidelines: [] as Guideline[],
    decorItems: [] as DecorItem[],
    stationeryItems: [] as StationeryItemSearch[],
    quotes: [] as VendorQuote[],
    contributors: [] as Contributor[],
    bundles: [] as UnifiedBundle[],
    products: [] as Product[],
    venues: [] as Venue[],
    floorPlans: [] as FloorPlan[],
    inventoryItems: [] as InventoryItem[],
    vehicles: [] as Vehicle[],
    drivers: [] as Driver[],
    movements: [] as GuestAssignment[],
  });

  // Load global data on mount
  useEffect(() => {
    if (accountScope) return;
    const loadGlobalData = async () => {
      try {
        // Synchronous sources (localStorage-backed) — instant
        const tasks = loadTasks().filter(t => !t.archived);
        const favours = loadInventoryItems().filter(i => i.category === 'favour');
        // Everything else in the shipping/inventory ledger that isn't a favour.
        const inventoryItems = loadInventoryItems().filter(i => i.category !== 'favour');
        const decorItems = loadDecorItems();
        const guidelines = loadGuidelines().filter(g => !g.archived);
        const songsInitial = loadSongs(); // may be empty until API returns

        // Set synchronous data immediately so search works right away
        setGlobalData(prev => ({
          ...prev,
          tasks,
          favours,
          inventoryItems,
          decorItems,
          guidelines,
          songs: songsInitial,
        }));

        // Async sources — load in parallel without blocking the above.
        // Every entry is individually failure-tolerant (allSettled + .catch),
        // so one dead endpoint never takes the whole index down.
        const [
          vendors, songsResult, guidelinesResult,
          decorResult, stationeryResult, menuResult, bundlesResult,
          productsResult, venuesResult, floorPlansResult, vehiclesResult,
          driversResult, movementsResult,
        ] = await Promise.allSettled([
          loadVendorData(),
          refreshSongsFromApi().catch(() => [] as Song[]),
          refreshGuidelinesFromApi().catch(() => [] as Guideline[]),
          refreshDecorFromApi().catch(() => [] as DecorItem[]),
          apiGet<StationeryItemSearch[] | { items: StationeryItemSearch[] }>(
            planningPath('/stationery')
          ).catch(() => null),
          refreshMenuFromApi().catch(() => []),
          refreshBundlesFromApi().catch(() => [] as UnifiedBundle[]),
          refreshProductsFromApi().catch(() => [] as Product[]),
          getVenues().catch(() => [] as Venue[]),
          getFloorPlans().catch(() => [] as FloorPlan[]),
          refreshVehiclesFromApi().catch(() => [] as Vehicle[]),
          refreshDriversFromApi().catch(() => [] as Driver[]),
          refreshAssignmentsFromApi().catch(() => [] as GuestAssignment[]),
        ]);

        const rawStationery = stationeryResult.status === 'fulfilled' ? stationeryResult.value : null;
        const stationeryItems: StationeryItemSearch[] = rawStationery
          ? (Array.isArray(rawStationery) ? rawStationery : (rawStationery as any).items ?? [])
              .filter((s: StationeryItemSearch) => !s.archived)
          : [];

        // Menu items live nested inside categories — flatten them so a search
        // for a dish name matches directly.
        const menuItems: MenuItemSearch[] =
          menuResult.status === 'fulfilled'
            ? menuResult.value.flatMap(cat =>
                (cat.items ?? [])
                  .filter(mi => !mi.archived)
                  .map((mi, idx) => ({
                    id: mi.id ?? `${cat.tab}-${cat.title}-${idx}`,
                    name: mi.name,
                    category: cat.title,
                    tab: cat.tab,
                    desc: mi.desc ?? '',
                    tags: mi.tags ?? [],
                    allowance: mi.allowance,
                  }))
              )
            : [];

        setGlobalData(prev => ({
          ...prev,
          vendors: vendors.status === 'fulfilled' ? vendors.value : [],
          menuItems,
          songs: songsResult.status === 'fulfilled' ? songsResult.value : prev.songs,
          guidelines: guidelinesResult.status === 'fulfilled' ? guidelinesResult.value.filter(g => !g.archived) : prev.guidelines,
          decorItems: decorResult.status === 'fulfilled' ? decorResult.value : prev.decorItems,
          stationeryItems,
          bundles: bundlesResult.status === 'fulfilled'
            ? bundlesResult.value.filter(b => !b.archived) : [],
          products: productsResult.status === 'fulfilled'
            ? productsResult.value.filter(p => !p.archived) : [],
          venues: venuesResult.status === 'fulfilled' ? venuesResult.value : [],
          floorPlans: floorPlansResult.status === 'fulfilled' ? floorPlansResult.value : [],
          vehicles: vehiclesResult.status === 'fulfilled'
            ? vehiclesResult.value.filter(v => !v.archived) : [],
          drivers: driversResult.status === 'fulfilled'
            ? driversResult.value.filter(d => !d.archived) : [],
          movements: movementsResult.status === 'fulfilled'
            ? movementsResult.value.filter(a => !a.archived) : [],
        }));
      } catch (error) {
        console.error('Error loading global search data:', error);
      }
    };

    loadGlobalData();

    // Re-index tasks when task data changes
    const onTasksChange = () => {
      setGlobalData(prev => ({
        ...prev,
        tasks: loadTasks().filter(t => !t.archived),
      }));
    };
    window.addEventListener('anojeje:tasks-changed', onTasksChange);

    // Re-index vendors when background refresh completes
    const onVendorsRefreshed = (e: Event) => {
      const fresh = (e as CustomEvent<ParsedVendor[]>).detail;
      if (Array.isArray(fresh) && fresh.length) {
        setGlobalData(prev => ({ ...prev, vendors: fresh }));
      }
    };
    window.addEventListener('anojeje:vendors-refreshed', onVendorsRefreshed);

    return () => {
      window.removeEventListener('anojeje:tasks-changed', onTasksChange);
      window.removeEventListener('anojeje:vendors-refreshed', onVendorsRefreshed);
    };
  }, [accountScope]);

  // Financial data is loaded in its own effect, keyed on the permission, so
  // that a user who may not see money never even fetches it. It re-runs once
  // the role resolves out of AuthContext's loading state, and clears the
  // cached financial slices if the permission is ever revoked.
  useEffect(() => {
    if (accountScope) return;
    if (!canViewFinancials) {
      setGlobalData(prev => ({
        ...prev,
        budgetCategories: [],
        quotes: [],
        contributors: [],
      }));
      return;
    }

    let cancelled = false;

    const loadFinancialData = async () => {
      const [budgetResult, quotesResult, contributorsResult] =
        await Promise.allSettled([
          refreshBudgetFromApi(),
          loadQuotes().catch(() => [] as VendorQuote[]),
          loadContributors().catch(() => [] as Contributor[]),
        ]);

      if (cancelled) return;

      setGlobalData(prev => ({
        ...prev,
        budgetCategories: budgetResult.status === 'fulfilled' ? budgetResult.value : prev.budgetCategories,
        quotes: quotesResult.status === 'fulfilled' ? quotesResult.value : [],
        contributors: contributorsResult.status === 'fulfilled'
          ? contributorsResult.value.filter(c => !c.archived) : [],
      }));
    };

    loadFinancialData().catch(error => {
      console.error('Error loading financial search data:', error);
    });

    return () => {
      cancelled = true;
    };
  }, [canViewFinancials, accountScope]);

  // Perform search when query changes
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    if (accountScope) {
      setResults(searchAccountRecords(query, accountData, { canViewFinancials }));
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    const q = query.toLowerCase().trim();
    const searchResults: SearchResult[] = [];

    // Per-type cap: with ~27 indexed entity types, an uncapped type (vendors,
    // guests) would consume the whole 30-result budget and hide everything
    // that comes after it — which is exactly why searching a vendor's name
    // never surfaced that vendor's quotes.
    const perTypeCount: Record<string, number> = {};
    const push = (result: SearchResult) => {
      const seen = perTypeCount[result.type] ?? 0;
      if (seen >= PER_TYPE_LIMIT) return;
      perTypeCount[result.type] = seen + 1;
      searchResults.push(result);
    };

    // ── Vendors ──────────────────────────────────────────────────────────────
    globalData.vendors.forEach(vendor => {
      if (genericMatch(vendor as unknown as Record<string, unknown>, q)) {
        const fmtService = (s: unknown) => {
          const raw = typeof s === 'string' ? s : (s as any)?.name ?? String(s ?? '');
          return raw.replace(/([a-z])([A-Z])/g, '$1 $2');
        };
        const serviceDesc = vendor.services.slice(0, 2).map(fmtService).join(', ');
        const locationTag = vendor.baseLocation ? vendor.baseLocation : '';
        push({
          id: `vendor-${vendor.id}`,
          type: 'vendor',
          title: String(vendor.name ?? ''),
          description: [serviceDesc, locationTag].filter(Boolean).join(' · '),
          category: fmtService(vendor.services[0]) || 'Vendor',
          data: vendor,
        });
      }
    });

    // ── Vendor quotes ─────────────────────────────────────────────────────────
    // Deliberately indexed straight after vendors: the whole point is that
    // typing a vendor's name surfaces that vendor's bids alongside the vendor.
    // FINANCIAL — gated.
    if (canViewFinancials) {
      const categoryNameById = new Map(
        globalData.budgetCategories.map(c => [String(c.id), c.name])
      );
      globalData.quotes.forEach(quote => {
        const categoryName = categoryNameById.get(String(quote.categoryId)) ?? '';
        const view = { ...quote, categoryName, displayId: `QT-${quote.id}` } as unknown as Record<string, unknown>;
        if (genericMatch(view, q)) {
          push({
            id: `quote-${quote.id}`,
            type: 'quote',
            title: quote.vendorName || `Quote ${recordId('QT', quote.id)}`,
            description: [categoryName, quote.status, ngn(quote.totalAmount)]
              .filter(Boolean).join(' · '),
            category: 'Quote',
            data: quote,
          });
        }
      });
    }

    // ── Budget categories ─────────────────────────────────────────────────────
    // FINANCIAL — gated.
    if (canViewFinancials) {
      globalData.budgetCategories.forEach(cat => {
        if (genericMatch(cat as unknown as Record<string, unknown>, q)) {
          push({
            id: `budget-${cat.id}`,
            type: 'budget',
            title: cat.name,
            description: `Budget: ${ngn(cat.planned_amount)}`,
            category: 'Budget',
            data: cat,
          });
        }
      });
    }

    // ── Payments ──────────────────────────────────────────────────────────────
    // Payments are nested budget-category → vendor line → payments[], so they
    // ride along on the already-gated budget fetch. FINANCIAL — gated.
    if (canViewFinancials) {
      const payments: PaymentSearch[] = globalData.budgetCategories.flatMap(cat =>
        (cat.vendors ?? []).flatMap(v =>
          (v.payments ?? []).map(p => ({
            id: p.id,
            amount: p.amount,
            date: p.date,
            method: p.method,
            status: p.status,
            reference: p.reference,
            paidBy: p.paid_by,
            notes: p.notes,
            vendorName: v.vendor_name,
            categoryName: cat.name,
          }))
        )
      );
      payments.forEach(payment => {
        if (genericMatch(payment as unknown as Record<string, unknown>, q)) {
          push({
            id: `payment-${payment.id}`,
            type: 'payment',
            title: `${ngn(payment.amount)} — ${payment.vendorName}`,
            description: [payment.categoryName, payment.status, payment.date, payment.reference]
              .filter(Boolean).join(' · '),
            category: 'Payment',
            data: payment,
          });
        }
      });
    }

    // ── Contributors ──────────────────────────────────────────────────────────
    // FINANCIAL — gated.
    if (canViewFinancials) {
      globalData.contributors.forEach(contributor => {
        if (genericMatch(contributor as unknown as Record<string, unknown>, q)) {
          push({
            id: `contributor-${contributor.id}`,
            type: 'contributor',
            title: contributor.name,
            description: [
              contributor.role,
              `contributed ${ngn(contributor.total_contributed)}`,
              contributor.reimbursement_status,
            ].filter(Boolean).join(' · '),
            category: 'Contributor',
            data: contributor,
          });
        }
      });
    }

    // ── Menu items ────────────────────────────────────────────────────────────
    globalData.menuItems.forEach(item => {
      if (genericMatch(item as unknown as Record<string, unknown>, q)) {
        push({
          id: `menu-${item.id}`,
          type: 'menu',
          title: item.name,
          description: [
            item.category,
            item.tab,
            // Per-head allowance is money — omitted for financially-gated users.
            canViewFinancials && item.allowance != null ? ngn(item.allowance) : '',
          ].filter(Boolean).join(' · '),
          category: 'Menu',
          data: item,
        });
      }
    });

    // ── Tasks ─────────────────────────────────────────────────────────────────
    globalData.tasks.forEach(task => {
      if (genericMatch(task as unknown as Record<string, unknown>, q)) {
        push({
          id: `task-${task.id}`,
          type: 'task',
          title: task.task,
          description: `${task.status} · ${task.priority}${task.milestone ? ` · ${task.milestone}` : ''}`,
          category: 'Task',
          data: task,
        });
      }
    });

    // ── Favours (inventory items) ─────────────────────────────────────────────
    globalData.favours.forEach(item => {
      if (genericMatch(item as unknown as Record<string, unknown>, q)) {
        push({
          id: `favour-${item.id}`,
          type: 'favour',
          title: item.name,
          description: [
            item.inventory_status,
            canViewFinancials ? `${formatDecimal(item.unit_price, item.currency)} × ${item.stock}` : `× ${item.stock}`,
          ].filter(Boolean).join(' · '),
          category: 'Favour',
          data: item,
        });
      }
    });

    // ── Songs ─────────────────────────────────────────────────────────────────
    globalData.songs.forEach(song => {
      if (genericMatch(song as unknown as Record<string, unknown>, q)) {
        push({
          id: `song-${song.id ?? song.name}`,
          type: 'song',
          title: song.name,
          description: `${song.artist}${song.genre ? ` · ${song.genre}` : ''} · ${song.type}`,
          category: 'Music',
          data: song,
        });
      }
    });

    // ── Guidelines ────────────────────────────────────────────────────────────
    globalData.guidelines.forEach(guideline => {
      if (genericMatch(guideline as unknown as Record<string, unknown>, q)) {
        push({
          id: `guideline-${guideline.id}`,
          type: 'guideline',
          title: guideline.title,
          description: `${guideline.category} · ${guideline.items?.length ?? 0} items`,
          category: 'Guideline',
          data: guideline,
        });
      }
    });

    // ── Decor items ───────────────────────────────────────────────────────────
    globalData.decorItems.forEach(item => {
      if (genericMatch(item as unknown as Record<string, unknown>, q)) {
        push({
          id: `decor-${item.id}`,
          type: 'decor',
          title: item.area || item.decor_element,
          description: `${item.category} · ${item.decor_element || '—'} · ${item.status}`,
          category: 'Decor',
          data: item,
        });
      }
    });

    // ── Stationery items ──────────────────────────────────────────────────────
    globalData.stationeryItems.forEach(item => {
      if (genericMatch(item as unknown as Record<string, unknown>, q)) {
        push({
          id: `stationery-${item.id}`,
          type: 'stationery',
          title: item.name,
          description: [
            item.category,
            item.print_status && item.print_status !== 'not-started' ? `print: ${item.print_status}` : item.design_status,
            item.assigned_to,
          ].filter(Boolean).join(' · '),
          category: 'Stationery',
          data: item,
        });
      }
    });

    // ── Bundles / packages ────────────────────────────────────────────────────
    globalData.bundles.forEach(bundle => {
      if (genericMatch(bundle as unknown as Record<string, unknown>, q)) {
        push({
          id: `bundle-${bundle.id}`,
          type: 'bundle',
          title: bundle.name,
          description: [
            bundle.types?.join(', '),
            `${bundle.items?.length ?? 0} items`,
            // Bundle price is money — omitted for financially-gated users.
            canViewFinancials && bundle.price ? ngn(bundle.price) : '',
          ].filter(Boolean).join(' · '),
          category: 'Bundle',
          data: bundle,
        });
      }
    });

    // ── Catalogue products ────────────────────────────────────────────────────
    globalData.products.forEach(product => {
      if (genericMatch(product as unknown as Record<string, unknown>, q)) {
        push({
          id: `product-${product.id}`,
          type: 'product',
          title: product.name,
          description: [
            product.types?.join(', '),
            product.suppliers?.length ? `${product.suppliers.length} suppliers` : '',
          ].filter(Boolean).join(' · '),
          category: 'Catalogue',
          data: product,
        });
      }
    });

    // ── Venues ────────────────────────────────────────────────────────────────
    globalData.venues.forEach(venue => {
      if (genericMatch(venue as unknown as Record<string, unknown>, q)) {
        push({
          id: `venue-${venue.id}`,
          type: 'venue',
          title: venue.name,
          description: [venue.address, venue.capacity ? `capacity ${venue.capacity}` : '']
            .filter(Boolean).join(' · '),
          category: 'Venue',
          data: venue,
        });
      }
    });

    // ── Floor plans ───────────────────────────────────────────────────────────
    {
      const venueNameById = new Map(globalData.venues.map(v => [v.id, v.name]));
      globalData.floorPlans.forEach(plan => {
        const venueName = venueNameById.get(plan.venue_id) ?? '';
        const view = { ...plan, venueName } as unknown as Record<string, unknown>;
        if (genericMatch(view, q)) {
          push({
            id: `floorplan-${plan.id}`,
            type: 'floorplan',
            title: plan.name,
            description: [venueName, 'Floor plan'].filter(Boolean).join(' · '),
            category: 'Floor Plan',
            data: plan,
          });
        }
      });
    }

    // ── Inventory items (non-favour) ──────────────────────────────────────────
    globalData.inventoryItems.forEach(item => {
      if (genericMatch(item as unknown as Record<string, unknown>, q)) {
        push({
          id: `inventory-${item.id}`,
          type: 'inventory',
          title: item.name,
          description: [
            item.category,
            item.inventory_status,
            canViewFinancials ? `${formatDecimal(item.unit_price, item.currency)} × ${item.stock}` : `× ${item.stock}`,
          ].filter(Boolean).join(' · '),
          category: 'Inventory',
          data: item,
        });
      }
    });

    // ── Vehicles ──────────────────────────────────────────────────────────────
    globalData.vehicles.forEach(vehicle => {
      if (genericMatch(vehicle as unknown as Record<string, unknown>, q)) {
        push({
          id: `vehicle-${vehicle.id}`,
          type: 'vehicle',
          title: vehicle.name,
          description: [vehicle.type, vehicle.license_plate, vehicle.status, `seats ${vehicle.capacity}`]
            .filter(Boolean).join(' · '),
          category: 'Vehicle',
          data: vehicle,
        });
      }
    });

    // ── Drivers ───────────────────────────────────────────────────────────────
    globalData.drivers.forEach(driver => {
      if (genericMatch(driver as unknown as Record<string, unknown>, q)) {
        push({
          id: `driver-${driver.id}`,
          type: 'driver',
          title: driver.name,
          description: [driver.phone, driver.email].filter(Boolean).join(' · ') || 'Driver',
          category: 'Driver',
          data: driver,
        });
      }
    });

    // ── Guest movements (logistics assignments) ───────────────────────────────
    globalData.movements.forEach(movement => {
      if (genericMatch(movement as unknown as Record<string, unknown>, q)) {
        push({
          id: `movement-${movement.id}`,
          type: 'movement',
          title: movement.guest_name,
          description: [
            movement.movement_type ?? 'pickup',
            `${movement.pickup_location} → ${movement.dropoff_location}`,
            movement.status,
          ].filter(Boolean).join(' · '),
          category: 'Movement',
          data: movement,
        });
      }
    });

    setResults(searchResults.slice(0, MAX_RESULTS));
    setIsSearching(false);
  }, [query, globalData, canViewFinancials, accountScope, accountData]);

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    setPageFilter('');
  };

  return (
    <SearchContext.Provider
      value={{
        query,
        setQuery,
        results,
        isSearching,
        clearSearch,
        pageFilter,
        setPageFilter,
        globalData,
        canViewFinancials,
        accountScope,
        accountStatus,
        openAccountResult,
      }}
    >
      <PageFilterContext.Provider value={pageFilter}>
        {children}
      </PageFilterContext.Provider>
    </SearchContext.Provider>
  );
};
